
import { useEffect, useState } from 'react';
import Wrapper from '../../../Wrapper/Wrapper';
import PortfolioManagementMain from './PortfolioManagementMain';
import { getMasterDetail, getMasterThesis } from '@/controllers/gridManager/masterTransManager/masterTransManager';
import RenderProfitLoss from '../../ShareMasterFollower/RenderProfitLoss';
import EditMasterInfoModal from './EditMasterInfoModal';
import UserInfoView from '../../ShareMasterFollower/UserInfoView/UserInfoView';
import DeleteMasterAccount from './DeleteMasterAccount/DeleteMasterAccount';
import { formatDate } from '@/utilities';
import { useCosInfo } from '@/controllers/gridManager/accountsManager/accountManager';
import { ColorButton } from '@/views/elements/button/ButtonElement';
import { useTranslation } from 'react-i18next';
import { useGetCosConfig } from '@/controllers/gridManager/appManager/appManager';
import RenderUpDown from '../../ShareMasterFollower/RenderUpDown';

export default function PortfolioManagementComponent() {
    const { t } = useTranslation()
    const [masterDetail, setMasterDetail] = useState<{ [propertyName: string]: any; } | null>(null)
    const [masterThesis, setMasterThesis] = useState<{ [propertyName: string]: any; } | null>(null)
    const [isOpenEditMaster, setIsOpenEditMaster] = useState(false)
    const [isOpenDeleteMaster, setIsOpenDeleteMaster] = useState(false)
    const { cosInfo } = useCosInfo()
    const { cosConfig } = useGetCosConfig()
    const { minnetasset } = cosConfig

    useEffect(() => {
        loadData()
        loadThesisData()
    }, []);

    const loadData = async () => {
        const { isSuccess, data } = await getMasterDetail(cosInfo.refaccount);
        if (isSuccess) setMasterDetail(data)
    }

    const loadThesisData = async () => {
        const { isSuccess, data } = await getMasterThesis(cosInfo.refaccount);
        if (isSuccess) setMasterThesis(data)
    }

    const achivements = [
        {
            title: t('copytrade.portfolioManagement.navMaster'),
            value: masterDetail?.nav
        },
        {
            title: t('copytrade.portfolioManagement.navCopy'),
            value: masterDetail?.aum
        },
        {
            title: t('copytrade.portfolioManagement.bonusfeeamt'),
            value: masterDetail?.bonusfeeamt
        },
        {
            title: t('copytrade.portfolioManagement.pnl'),
            render: <RenderUpDown fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.pnl || 0} />
        },
        {
            title: '% ' + t('copytrade.portfolioManagement.pnl'),
            render: <RenderUpDown isPercentage fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.pnl_rate || 0} />
        },
        {
            title: t('copytrade.portfolioManagement.numberFollower'),
            value: masterDetail?.follower_number
        },
        {
            title: t('copytrade.portfolioManagement.numberCopied'),
            value: masterDetail?.copier_number
        },
        {
            title: t('copytrade.portfolioManagement.riskScore'),
            value: masterDetail?.risk_point
        },
        {
            title: t('copytrade.portfolioManagement.eMasterScore'),
            value: masterDetail?.score
        },
        {
            title: t('copytrade.portfolioManagement.minInvest'),
            value: minnetasset
        },
        {
            title: t('copytrade.portfolioManagement.onDay'),
            render: <RenderProfitLoss fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.dr || 0} />
        },
        {
            title: t('copytrade.portfolioManagement.effective1m'),
            render: <RenderProfitLoss fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.r_1_mon || 0} />
        },
        {
            title: t('copytrade.portfolioManagement.effective3m'),
            render: <RenderProfitLoss fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.r_3_mon || 0} />
        },
        {
            title: t('copytrade.portfolioManagement.effectiveFromBegin'),
            render: <RenderProfitLoss fontSizeElement='14px' fontWeightElement={500} data={masterDetail?.ar || 0} />
        },
        {
            title: t('copytrade.portfolioManagement.bonusFee'),
            value: masterDetail?.feerate
        },
    ]

    const actions = [
        {
            label: t('copytrade.portfolioManagement.deleteMasterAccount'),
            onClick: () => {
                setIsOpenDeleteMaster(true)
            },
            disabled: cosInfo?.status === 'WC',
            color: 'Primary' as ColorButton
        },
        {
            label: t('copytrade.portfolioManagement.edit'),
            onClick: () => {
                setIsOpenEditMaster(true)
            }
        },
    ]

    const handleEditMasterClose = async () => {
        setIsOpenEditMaster(false)
    }

    const handleEditMasterDone = async () => {
        await Promise.all([
            loadData(),
            loadThesisData()
        ]);
        setIsOpenEditMaster(false)
    }

    return (
        <>
            <Wrapper>
                <UserInfoView
                    achivements={achivements}
                    section={t('copytrade.portfolioManagement.section')}
                    title={t('copytrade.portfolioManagement.title')}
                    name={masterDetail?.mt_fullname || cosInfo.fullname}
                    registerDate={formatDate(masterDetail?.reg_date)}
                    rank={masterDetail?.mt_rank ?? "M"}
                    actions={actions}
                    custodycd={masterDetail?.custodycd || cosInfo.custodycd}
                    linkaccount={cosInfo?.linkaccount}
                />
            </Wrapper>
            <Wrapper>
                <PortfolioManagementMain />
            </Wrapper>
            {isOpenEditMaster && <EditMasterInfoModal preData={{
                shortInvestThesis: masterThesis?.shortinvestthesis,
                investThesis: masterThesis?.investthesis,
                minInvestAmt: masterThesis?.mininvestamt,
                feeRate: masterThesis?.feerate,
                commissionRate: masterThesis?.commisionrate,
            }} open={isOpenEditMaster} onClose={handleEditMasterClose} onDone={handleEditMasterDone} />}
            {isOpenDeleteMaster && <DeleteMasterAccount
                open={isOpenDeleteMaster}
                handleClose={() => setIsOpenDeleteMaster(false)}
                title=''
                data={masterDetail}
            />}
        </>
    );
}